import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import SEO from "../components/SEO";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import SignaturePad from "../components/SignaturePad";
import IDScanner from "../components/IDScanner";
import { toast } from "sonner";
import { CheckCircle2, FileText, Loader2, ShieldCheck } from "lucide-react";

function Field({ label, children }) {
  return (
    <div>
      <Label className="block text-sm font-semibold text-gray-900 mb-2">{label}</Label>
      {children}
    </div>
  );
}

export default function Sign() {
  const { id, role } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t");

  const [agreement, setAgreement] = useState(null);
  const [error, setError] = useState(null);
  const [sig, setSig] = useState(null);

  // Load via public token if present, else legacy private flow (same-session)
  useEffect(() => {
    const run = async () => {
      try {
        if (token) {
          const a = await api.getAgreementPublic(id, token, role);
          setAgreement(a);
        } else {
          const a = await api.getAgreement(id);
          setAgreement(a);
        }
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not load agreement");
      }
    };
    run();
  }, [id, role, token]);

  const setMyField = async (key, value) => {
    // Optimistic local update + persist
    setAgreement((prev) => {
      const next = { ...(prev?.[role] || {}), [key]: value };
      const merged = { ...prev, [role]: next };
      if (prev?.id) {
        if (token) {
          api.updateAgreementPublic(id, token, role, { [role]: next }).catch(() => {});
        } else {
          api.updateAgreement(id, { [role]: next }).catch(() => {});
        }
      }
      return merged;
    });
  };

  const setLegalAck = async (v) => {
    try {
      if (token) {
        const fresh = await api.updateAgreementPublic(id, token, role, { legal_acknowledged: v });
        setAgreement(fresh);
      } else {
        const fresh = await api.updateAgreement(id, { legal_acknowledged: v });
        setAgreement(fresh);
      }
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const handleIdExtract = async (extracted) => {
    const cur = agreement?.[role] || {};
    const merged = {
      ...cur,
      full_name: extracted.full_name || cur.full_name,
      address: extracted.address || cur.address,
      city: extracted.city || cur.city,
      state: extracted.state || cur.state,
      zip: extracted.zip || cur.zip,
      id_number_optional: extracted.id_number || cur.id_number_optional,
    };
    try {
      const fresh = token
        ? await api.updateAgreementPublic(id, token, role, { [role]: merged })
        : await api.updateAgreement(id, { [role]: merged });
      setAgreement(fresh);
      toast.success("Fields autofilled — please review");
    } catch (e) {
      toast.error("Could not save");
    }
  };

  const handleSign = async () => {
    if (!agreement.legal_acknowledged) return toast.error("Please confirm the legal acknowledgment");
    if (!sig) return toast.error("Please add a signature");
    const me = agreement[role] || {};
    if (!(me.full_name && me.address && me.city && me.state && me.zip)) return toast.error("Please complete your information first");
    try {
      const fresh = token
        ? await api.signPublic(id, token, role, sig)
        : await api.sign(id, { role, ...sig });
      setAgreement(fresh);
      toast.success("Signed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Signing failed");
    }
  };

  if (error) {
    return (
      <Layout>
        <SEO title="Sign Bill of Sale | CarSaleDoc" description="Sign your bill of sale." path="/sign" noindex />
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <div className="csd-card p-8">
            <div className="font-heading text-xl font-bold mb-2">Link unavailable</div>
            <p className="text-sm text-[var(--csd-text-secondary)]">{error}</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!agreement) {
    return (
      <Layout>
        <SEO title="Sign Bill of Sale | CarSaleDoc" description="Sign your bill of sale." path="/sign" noindex />
        <div className="max-w-2xl mx-auto px-6 py-20 text-center"><Loader2 className="animate-spin mx-auto text-[var(--csd-primary)]" /></div>
      </Layout>
    );
  }

  const me = agreement[role] || {};
  const other = role === "buyer" ? "seller" : "buyer";
  const otherParty = agreement[other] || {};
  const signed = !!me.signature;
  const completed = agreement.status === "completed";

  return (
    <Layout>
      <SEO title="Sign Bill of Sale | CarSaleDoc" description="Sign your bill of sale." path="/sign" noindex />
      <div className="max-w-3xl mx-auto px-4 py-10">
        <div className="csd-card p-6 sm:p-8 space-y-6">
          <div>
            <div className="inline-flex items-center gap-2 mb-3 text-xs uppercase tracking-widest font-semibold text-[var(--csd-primary)] bg-[var(--csd-primary-light)] px-3 py-1 rounded-full">
              <ShieldCheck size={14} />Sign as {role}
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Review and sign</h1>
            <p className="text-sm text-[var(--csd-text-secondary)] mt-1">
              <b>{otherParty.full_name || "The other party"}</b> ({other}) has prepared a bill of sale for the <b>{agreement.vehicle?.year} {agreement.vehicle?.make} {agreement.vehicle?.model}</b>{agreement.state ? ` in ${agreement.state}` : ""}. Fill in your information and sign below.
            </p>
          </div>

          {/* Read-only PDF preview */}
          <div className="csd-card overflow-hidden">
            <iframe title="preview" src={api.pdfUrl(id)} className="w-full" style={{ height: "55vh" }} data-testid="sign-pdf-preview" />
          </div>

          {!signed && !completed && (
            <>
              {/* ID Scan */}
              <IDScanner agreementId={id} role={role} scanFn={api.scanId} onExtract={handleIdExtract} />

              {/* My info form */}
              <div className="space-y-4">
                <h3 className="font-heading font-bold text-lg">Your information</h3>
                <Field label="Full legal name"><Input data-testid={`${role}-full-name`} value={me.full_name || ""} onChange={(e) => setMyField("full_name", e.target.value)} /></Field>
                <Field label="Address"><Input data-testid={`${role}-address`} value={me.address || ""} onChange={(e) => setMyField("address", e.target.value)} /></Field>
                <div className="grid sm:grid-cols-3 gap-4">
                  <Field label="City"><Input data-testid={`${role}-city`} value={me.city || ""} onChange={(e) => setMyField("city", e.target.value)} /></Field>
                  <Field label="State"><Input data-testid={`${role}-state`} value={me.state || ""} onChange={(e) => setMyField("state", e.target.value)} /></Field>
                  <Field label="ZIP code"><Input data-testid={`${role}-zip`} value={me.zip || ""} onChange={(e) => setMyField("zip", e.target.value)} /></Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Phone"><Input data-testid={`${role}-phone`} value={me.phone || ""} onChange={(e) => setMyField("phone", e.target.value)} /></Field>
                  <Field label="Email"><Input data-testid={`${role}-email`} type="email" value={me.email || ""} onChange={(e) => setMyField("email", e.target.value)} /></Field>
                </div>
                <Field label="ID / license number (optional)"><Input value={me.id_number_optional || ""} onChange={(e) => setMyField("id_number_optional", e.target.value)} /></Field>
              </div>

              <label className="flex items-start gap-3 p-4 rounded-xl border border-gray-200 cursor-pointer">
                <Checkbox checked={!!agreement.legal_acknowledged} onCheckedChange={(v) => setLegalAck(!!v)} data-testid="sign-legal-ack" />
                <span className="text-sm text-[var(--csd-text-secondary)]">
                  I understand that CarSaleDoc is not a law firm and this document does not replace official DMV forms or legal advice.
                </span>
              </label>

              <div>
                <h3 className="font-heading font-bold text-lg mb-3">Your signature</h3>
                <SignaturePad role={role} onChange={setSig} />
                <Button className="csd-btn-primary w-full mt-4 rounded-xl" onClick={handleSign} data-testid="sign-submit-btn">
                  Sign as {role}
                </Button>
              </div>
            </>
          )}

          {(signed || completed) && (
            <div className="rounded-xl bg-[var(--csd-primary-light)]/40 border border-[var(--csd-primary-light)] p-5">
              <div className="flex items-center gap-2 text-[var(--csd-primary)]">
                <CheckCircle2 /> <span className="font-semibold">
                  {completed ? "Agreement completed. PDF emailed to both parties." : "Signed."}
                </span>
              </div>
              <a href={api.pdfUrl(id)} target="_blank" rel="noreferrer">
                <Button className="csd-btn-primary mt-3 rounded-xl" data-testid="sign-download-pdf"><FileText size={14} className="mr-2" />Download PDF</Button>
              </a>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
